import SettingsIcon from '@mui/icons-material/Settings';
import {
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Switch,
  FormControlLabel,
  Alert,
  Box,
} from '@mui/material';
import { Can } from 'core/components/permissions/Can';
import { useModuleAccess } from 'hooks/useModuleAccess';
import { observer } from 'mobx-react-lite';
import { MODULE_NAMES } from 'models/shop/moduleTypes';
import { useContext, useState } from 'react';
import * as shopService from 'services/shopService';
import StoreContext from 'stores/context/StoreContext';
import type { ModuleType } from 'models/shop/moduleTypes';
import type { FC } from 'react';

/**
 * Компонент для настройки модулей магазина
 */
const ModulesSettings: FC = observer(() => {
  const { shopStore, notifyStore } = useContext(StoreContext);
  const { getEnabledModules } = useModuleAccess();
  const [isUpdating, setIsUpdating] = useState(false);

  const enabledModules = getEnabledModules();
  const shop = shopStore.shop;

  /**
   * Обработчик изменения состояния модуля
   *
   * @param moduleType - Тип модуля
   * @param enabled - Новое состояние модуля
   */
  const handleModuleToggle = async (
    moduleType: ModuleType,
    enabled: boolean
  ): Promise<void> => {
    if (shop?.settings?.enabledModules == null || isUpdating) {
      return;
    }

    setIsUpdating(true);

    try {
      const updatedSettings = {
        ...shop.settings,
        enabledModules: {
          ...shop.settings.enabledModules,
          [moduleType]: enabled,
        },
      };

      await shopService.updateShopAsync(shop.id, {
        name: shop.name,
        description: shop.description,
        settings: updatedSettings,
        mangoVpbxApiKey: shop.mangoVpbxApiKey,
        mangoVpbxApiSalt: shop.mangoVpbxApiSalt,
      });

      // Обновляем данные в store
      shopStore.updateShopInfo();

      notifyStore.showNotify(
        `Модуль "${MODULE_NAMES[moduleType]}" ${enabled ? 'включен' : 'отключен'}`,
        'success'
      );
    } catch (error) {
      console.error('Ошибка при обновлении модуля:', error);
      notifyStore.showNotify('Ошибка при обновлении настроек модулей', 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  if (shop?.settings?.enabledModules == null) {
    return (
      <Alert severity='warning'>
        Настройки модулей недоступны. Обратитесь к администратору.
      </Alert>
    );
  }

  const modules: ModuleType[] = ['discounts', 'payouts', 'images', 'files'];

  return (
    <Can I='read' a='ShopModules'>
      <Box>
        <Typography variant='h6'>Модули системы</Typography>
        <Divider sx={{ my: 2 }} />

        <Alert severity='info' sx={{ mb: 2 }}>
          Управление доступностью функциональных модулей системы. Отключенные модули будут
          скрыты из интерфейса.
        </Alert>

        <List>
          {modules.map((moduleType) => {
            const isEnabled = Boolean(shop.settings?.enabledModules?.[moduleType]);

            return (
              <Can key={moduleType} I='update' a='ShopModules'>
                <ListItem>
                  <ListItemIcon>
                    <SettingsIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={MODULE_NAMES[moduleType]}
                    secondary={`Модуль "${MODULE_NAMES[moduleType]}" ${
                      isEnabled ? 'активен' : 'отключен'
                    }`}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isEnabled}
                        disabled={isUpdating}
                        onChange={(event) => {
                          void handleModuleToggle(moduleType, event.target.checked);
                        }}
                      />
                    }
                    label=''
                  />
                </ListItem>
              </Can>
            );
          })}
        </List>

        <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
          Активных модулей: {enabledModules.length} из {modules.length}
        </Typography>
      </Box>
    </Can>
  );
});

export default ModulesSettings;
